import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MdCloudUpload, MdDelete, MdCheckCircle, MdPhoto, MdClose } from "react-icons/md";
import { motion, AnimatePresence } from 'framer-motion';
import { setUpdateUser } from '../../state';

const EditUserPhoto = ({ setProfileImageUrl, setShowEditPopup }) => {
  const user = useSelector((state) => state.auth.user);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(user.profile_photo_url);
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();
  const [error, setError] = useState('');

  const handleFileChange = (event) => {
    const newImagefile = event.target.files[0];
    if (newImagefile) {
      handleNewFile(newImagefile);
    }
  };

  const handleNewFile = (file) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setCurrentImageUrl(URL.createObjectURL(file));
    } else {
      alert('Please select an image file.');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleNewFile(file);
  };

  const handleSave = async () => {
    if (!selectedFile) {
      setError('Please select a photo to upload');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    
    const imageBlob = new Blob([selectedFile], { type: selectedFile.type });
    
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    const newFileName = `profile.${fileExtension}`;
    const modifiedFile = new File([imageBlob], newFileName, {
      type: selectedFile.type,
    });
    
    formData.append('photo', modifiedFile);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/update-profile-photo/${user.id}`, {
        method: 'POST',
        headers: {
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined,
          'Api-Name': 'user-api',
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload photo');
      }

      const data = await response.json();
      
      // Update profile image URL in parent component and Redux store
      if (data.profile_photo_url) {
        setProfileImageUrl(data.profile_photo_url);
        dispatch(setUpdateUser({ ...user, profile_photo_url: data.profile_photo_url }));
      }
      
      // Close the popup
      setShowEditPopup(false);
    } catch (error) {
      console.error('Error uploading photo:', error);
      setError(error.message || 'Failed to upload photo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="relative overflow-hidden"
    >
      {/* Glossy Effect Overlays */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-soft-pink/10" />
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pri-white/5 to-transparent" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/10 to-transparent" />
      </div>

      <div className="relative p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-soft-pink to-pri-white">
            Change Profile Photo
          </h2>
          <button 
            onClick={() => setShowEditPopup(false)}
            className="text-pri-gray hover:text-pri-white transition-colors"
          >
            <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Current Image Preview */}
        <motion.div
          className="relative group cursor-pointer"
          whileHover={{ scale: 1.02 }}
        >
          <div className="relative bg-gradient-to-r from-sec-dark/80 to-pri-dark/80 rounded-xl p-4 border border-soft-pink/20 backdrop-blur-md">
            <img 
              src={currentImageUrl || 'https://via.placeholder.com/150'}
              alt="Preview" 
              className="w-32 h-32 sm:w-40 sm:h-40 mx-auto rounded-xl object-cover shadow-lg"
            />
          </div>
        </motion.div>

        {/* Upload Interface */}
        <motion.div
          className={`p-4 sm:p-6 border-2 border-dashed ${
            isDragging ? 'border-soft-pink bg-soft-pink/10' : 'border-soft-pink/20'
          } rounded-xl text-center transition-colors duration-300 bg-gradient-to-r from-sec-dark/80 to-pri-dark/80 backdrop-blur-md`}
          whileHover={{ scale: 1.01 }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            className="hidden"
            onChange={handleFileChange}
            accept="image/*"
            id="photo-upload"
          />
          <label
            htmlFor="photo-upload"
            className="cursor-pointer block"
          >
            <MdPhoto className="mx-auto text-3xl sm:text-4xl text-soft-pink mb-3" />
            <p className="text-sm sm:text-base text-pri-white mb-2">
              Drag and drop your image here or
              <span className="text-soft-pink font-medium ml-1">browse</span>
            </p>
            <p className="text-xs sm:text-sm text-pri-gray">
              Supports: JPG, PNG, GIF (Max 5MB)
            </p>
          </label>
        </motion.div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-4 sm:mt-6">
          <motion.button
            className={`flex-1 px-4 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base flex items-center justify-center ${
              isUploading
                ? 'bg-soft-pink/50 cursor-not-allowed'
                : 'bg-gradient-to-r from-soft-pink to-soft-pink/70 hover:shadow-lg'
            } text-black font-medium transition-all duration-300`}
            onClick={handleSave}
            disabled={isUploading}
            whileHover={{ scale: isUploading ? 1 : 1.02 }}
            whileTap={{ scale: isUploading ? 1 : 0.98 }}
          >
            {isUploading ? (
              <span className="flex items-center">
                <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Uploading...
              </span>
            ) : (
              <>
                <MdCloudUpload className="mr-2 text-lg sm:text-xl" /> Upload
              </>
            )}
          </motion.button>
          <motion.button
            className="flex-1 px-4 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-500 hover:text-red-400 font-medium flex items-center justify-center hover:shadow-lg transition-all duration-300"
            onClick={() => setShowEditPopup(false)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <MdDelete className="mr-2 text-lg sm:text-xl" /> Cancel
          </motion.button>
        </div>
      </div>

      {/* Success Popup */}
      <AnimatePresence>
        {showSuccessPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50"
          >
            <motion.div
              className="bg-gradient-to-b from-sec-dark/80 to-pri-dark/80 p-6 sm:p-8 rounded-2xl shadow-2xl text-center max-w-sm mx-4 border border-soft-pink/20 backdrop-blur-xl"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <MdCheckCircle className="text-green-500 text-4xl sm:text-6xl mx-auto mb-4" />
              </motion.div>
              <h3 className="text-xl sm:text-2xl font-bold text-pri-white mb-2">Success!</h3>
              <p className="text-sm sm:text-base text-pri-gray">Your profile photo has been updated successfully.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default EditUserPhoto;
